"use client";

import React from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCommandPaletteOptional } from "./command-palette-context";

/**
 * The header's search affordance. Opens the command palette and — the point of
 * it — SHOWS the keyboard shortcut, so the feature is discoverable instead of
 * being a hidden power-user chord nobody knows exists.
 *
 * Renders nothing when no palette provider is mounted (boundary pages), rather
 * than offering a button that cannot work.
 */
export function CommandPaletteTrigger({ className }: { className?: string }) {
  const palette = useCommandPaletteOptional();
  if (!palette) return null;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={palette.openPalette}
      aria-keyshortcuts="Control+K Meta+K"
      className={cn(
        "h-8 justify-start gap-2 bg-muted/40 px-2.5 font-normal text-muted-foreground hover:bg-accent hover:text-foreground",
        // Give the control real width on desktop so it reads as a search field,
        // and collapse to an icon button where the header is tight.
        "w-9 sm:w-auto sm:min-w-[15rem] lg:min-w-[18rem]",
        className
      )}
    >
      <Search className="h-4 w-4 shrink-0" aria-hidden />
      <span className="hidden flex-1 text-left sm:inline">Search or jump to…</span>
      <kbd className="ml-auto hidden select-none rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-block">
        {palette.shortcutLabel}
      </kbd>
      <span className="sr-only sm:hidden">Search</span>
    </Button>
  );
}
